import React, { useMemo, useCallback } from 'react';
import Animated from 'react-native-reanimated';
import { View } from 'react-native';
import { AppView, CardView, Indicator, withAnimatedNavigationHeader } from 'design-system';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import { assertUnreachable } from 'utils';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppSelector } from 'store/hooks';
import { useOrders } from '../hooks';
import * as selectors from '../hooks/selectors';
import EmptyState from '../components/EmptyState';
import ActivityItem from '../components/ActivityItem';
import { selectActiveTradingAccountId } from '../reducers/selectors';
import SwitchActivityItem from '../components/SwitchActivityItem';
const keyExtractor = (item) => item.id;
const getPageTitle = (tradingOrderType) => {
    switch (tradingOrderType) {
        case 'pending':
            return 'Pending orders';
        case 'queued':
            return 'Queued orders';
        case 'limitAndStop':
            return 'Limit and stop orders';
        default:
            assertUnreachable(tradingOrderType);
            return '';
    }
};
const emptyArr = [];
const OrdersScreen = withAnimatedNavigationHeader((props) => {
    const styles = useStyles(styleSet);
    const { tradingOrderType, assetId } = props.route.params;
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const infoObj = useMemo(() => ({
        assetId,
        tradingAccountId,
        orderType: tradingOrderType,
    }), [assetId, tradingAccountId, tradingOrderType]);
    const [, initialRequesting, onLoadNextPage] = useOrders(infoObj);
    const orders = selectors.useOrders(assetId, tradingOrderType, tradingAccountId);
    const ordersIndexLength = useMemo(() => (orders?.length ? orders.length - 1 : emptyArr), [orders?.length]);
    const renderItem = useCallback(({ item, index }) => {
        if (item.operation === 'switch') {
            return (<SwitchActivityItem item={item} withWrapper isFirstInSection={index === 0} isLastInSection={index === ordersIndexLength}/>);
        }
        return (<ActivityItem item={item} withWrapper isFirstInSection={index === 0} isLastInSection={index === ordersIndexLength}/>);
    }, [ordersIndexLength]);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, styles.contentContainer);
    const ListEmptyComponent = useMemo(() => {
        if (initialRequesting) {
            return (<CardView noMarginTop noMarginBottom style={styles.loaderCard}>
            <Indicator dark/>
          </CardView>);
        }
        return (<EmptyState orders={tradingOrderType === 'queued'} pendingOrders={tradingOrderType === 'pending'} limitAndStopOrders={tradingOrderType === 'limitAndStop'}/>);
    }, [initialRequesting, styles.loaderCard, tradingOrderType]);
    return (<AppView>
        <View style={[styles.innerContainer, styles.borderRadiuses]}>
          <Animated.FlatList data={orders} renderItem={renderItem} scrollEventThrottle={16} style={styles.scrollView} keyExtractor={keyExtractor} onEndReached={onLoadNextPage} onScroll={props.scrollHandler} contentContainerStyle={paddingBottom} ListEmptyComponent={ListEmptyComponent} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}/>
        </View>
      </AppView>);
}, ({ route, colors }) => ({
    title: getPageTitle(route.params.tradingOrderType),
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets(() => ({
    borderRadiuses: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
    },
    innerContainer: {
        flex: 1,
    },
    scrollView: {
        flex: 1,
    },
    contentContainer: {
        paddingTop: rem(16),
    },
    loaderCard: {
        padding: rem(156),
    },
}));
export default OrdersScreen;
